import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

export interface PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  itemLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSizeSelector = false,
  itemLabel = 'items',
  className = '',
  style,
  disabled = false,
}) => {
  const { t } = useLanguage();
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.max(1, Math.min(currentPage, totalPages));

  const startItem = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, totalItems);

  // Calculate page number array with ellipses
  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (safePage <= 4) {
      return [1, 2, 3, 4, 5, 'ellipsis-end', totalPages];
    }
    if (safePage >= totalPages - 3) {
      return [1, 'ellipsis-start', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, 'ellipsis-start', safePage - 1, safePage, safePage + 1, 'ellipsis-end', totalPages];
  }, [safePage, totalPages]);

  return (
    <div
      className={`pagination-container ${className}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '12px 16px',
        borderTop: '1px solid var(--line, #DCE3EF)',
        background: 'var(--white, #ffffff)',
        fontSize: '13px',
        color: 'var(--muted, #5B6781)',
        minHeight: '52px',
        ...style,
      }}
      role="navigation"
      aria-label={t('pagination.nav_aria', undefined, 'Pagination')}
    >
      {/* Left: Record summary & optional page size selector */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <span>
          {t(
            'pagination.showing',
            { start: startItem, end: endItem, total: totalItems, items: itemLabel },
            `Showing ${startItem}–${endItem} of ${totalItems} ${itemLabel}`
          )}
        </span>

        {(showPageSizeSelector || !!onPageSizeChange) && onPageSizeChange && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: 'var(--muted, #5B6781)' }}>
              {t('pagination.per_page', undefined, 'Per page:')}
            </span>
            <select
              value={pageSize}
              disabled={disabled}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label={t('pagination.select_per_page_aria', undefined, 'Select items per page')}
              style={{
                height: '30px',
                padding: '0 24px 0 8px',
                borderRadius: '6px',
                border: '1px solid var(--line, #DCE3EF)',
                background: '#ffffff url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M4 6l4 4 4-4\' fill=\'none\' stroke=\'%2364748b\' stroke-width=\'1.8\' stroke-linecap=\'round\'/%3E%3C/svg%3E") right 6px center/10px no-repeat',
                appearance: 'none',
                WebkitAppearance: 'none',
                fontSize: '12px',
                color: 'var(--ink, #0E1B33)',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right: Page controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
        {/* Previous Button */}
        <button
          type="button"
          disabled={safePage <= 1 || disabled}
          onClick={() => onPageChange(safePage - 1)}
          aria-label={t('pagination.prev_aria', undefined, 'Previous page')}
          style={{
            height: '32px',
            minWidth: '72px',
            padding: '0 10px',
            borderRadius: '6px',
            border: '1px solid var(--line, #DCE3EF)',
            background: safePage <= 1 || disabled ? 'var(--surface, #F5F8FC)' : '#ffffff',
            color: safePage <= 1 || disabled ? 'var(--faint, #8A94A8)' : 'var(--ink, #0E1B33)',
            cursor: safePage <= 1 || disabled ? 'not-allowed' : 'pointer',
            fontWeight: 500,
            fontSize: '12.5px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            transition: 'all 0.15s ease',
          }}
        >
          {t('pagination.previous', undefined, '‹ Previous')}
        </button>

        {/* Number Pills */}
        {pageNumbers.map((p, idx) => {
          if (p === 'ellipsis-start' || p === 'ellipsis-end') {
            return (
              <span
                key={`${p}-${idx}`}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--faint, #8A94A8)',
                  fontSize: '13px',
                }}
              >
                …
              </span>
            );
          }

          const pageNum = p as number;
          const isActive = pageNum === safePage;

          return (
            <button
              key={pageNum}
              type="button"
              disabled={disabled}
              onClick={() => onPageChange(pageNum)}
              aria-current={isActive ? 'page' : undefined}
              aria-label={t('pagination.page_aria', { page: pageNum }, `Page ${pageNum}`)}
              style={{
                height: '32px',
                minWidth: '32px',
                padding: '0 6px',
                borderRadius: '6px',
                border: isActive ? '1px solid var(--blue, #0D4FBD)' : '1px solid var(--line, #DCE3EF)',
                background: isActive ? 'var(--blue, #0D4FBD)' : '#ffffff',
                color: isActive ? '#ffffff' : 'var(--ink, #0E1B33)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontWeight: isActive ? 700 : 500,
                fontSize: '12.5px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              {pageNum}
            </button>
          );
        })}

        {/* Next Button */}
        <button
          type="button"
          disabled={safePage >= totalPages || disabled}
          onClick={() => onPageChange(safePage + 1)}
          aria-label={t('pagination.next_aria', undefined, 'Next page')}
          style={{
            height: '32px',
            minWidth: '60px',
            padding: '0 10px',
            borderRadius: '6px',
            border: '1px solid var(--line, #DCE3EF)',
            background: safePage >= totalPages || disabled ? 'var(--surface, #F5F8FC)' : '#ffffff',
            color: safePage >= totalPages || disabled ? 'var(--faint, #8A94A8)' : 'var(--ink, #0E1B33)',
            cursor: safePage >= totalPages || disabled ? 'not-allowed' : 'pointer',
            fontWeight: 500,
            fontSize: '12.5px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            transition: 'all 0.15s ease',
          }}
        >
          {t('pagination.next', undefined, 'Next ›')}
        </button>
      </div>
    </div>
  );
};

export interface UsePaginationResult<T> {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  paginatedItems: T[];
  goToPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  resetPage: () => void;
}

export function usePagination<T>(
  items: T[] = [],
  initialPage: number = 1,
  initialPageSize: number = 20
): UsePaginationResult<T> {
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageSize, setPageSizeState] = useState<number>(initialPageSize);

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Reset or clamp currentPage if items change or page exceeds bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, currentPage, pageSize]);

  const goToPage = (page: number) => {
    const clamped = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(clamped);
  };

  const setPageSize = (newSize: number) => {
    setPageSizeState(newSize);
    setCurrentPage(1);
  };

  const resetPage = () => {
    setCurrentPage(1);
  };

  return {
    currentPage,
    pageSize,
    totalPages,
    totalItems,
    paginatedItems,
    goToPage,
    setPageSize,
    resetPage,
  };
}
